/**
 * An idea's dimension scores as a radar: the profile's shape at a glance,
 * with the industry's average as a dashed polygon behind it when there is
 * one. The idea is the primary hue, the benchmark the muted ink; rings every
 * 20 points, the 60 and 80 band cut-offs dashed. Hovering or focusing a point
 * opens a card with its score, the benchmark, the difference, the weight and
 * the points it adds. Fewer than three scored dimensions draw nothing: a
 * radar needs an area. The Dimensions table is the precise, accessible view.
 */

import { useState } from 'react';

import { formatScore } from '@/ui/ScoreValue';

export interface RadarAxis {
  key: string;
  label: string;
  value: number;
  weight: number;
  points: number | null;
  benchmark: number | null;
}

const R = 80;
const RINGS = [20, 40, 60, 80, 100];
const VIEW = { x: -172, y: -106, w: 344, h: 212 };

/** Where a value on axis i of n sits; axis 0 points straight up, the rest run clockwise. */
function point(i: number, n: number, value: number) {
  const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
  const r = (R * value) / 100;
  return { x: Math.cos(angle) * r, y: Math.sin(angle) * r, cos: Math.cos(angle), sin: Math.sin(angle) };
}

const polygon = (values: number[]) =>
  values.map((v, i) => point(i, values.length, v)).map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

function signed(value: number) {
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return '0';
  return `${rounded > 0 ? '+' : '−'}${formatScore(Math.abs(rounded))}`;
}

/** Where an axis label sits: just past the outer ring, anchored away from the centre. */
function labelPlacement(i: number, n: number) {
  const end = point(i, n, 100);
  const anchor: 'middle' | 'start' | 'end' = Math.abs(end.cos) < 0.2 ? 'middle' : end.cos > 0 ? 'start' : 'end';
  const nudge = end.sin > 0.2 ? 10 : end.sin < -0.2 ? -3 : 5;
  return { x: end.x + end.cos * 8, y: end.y + end.sin * 8 + nudge, anchor };
}

function RadarPoint({
  axis,
  i,
  n,
  active,
  benchmarkText,
  setHover,
}: {
  axis: RadarAxis;
  i: number;
  n: number;
  active: boolean;
  benchmarkText: string;
  setHover: (key: string | null) => void;
}) {
  const p = point(i, n, axis.value);
  const label = labelPlacement(i, n);
  return (
    <g
      tabIndex={0}
      className="dim-radar-point"
      aria-label={`${axis.label}: ${axis.value} of 100${benchmarkText}, weight ${axis.weight}%`}
      onMouseEnter={() => setHover(axis.key)}
      onMouseLeave={() => setHover(null)}
      onFocus={() => setHover(axis.key)}
      onBlur={() => setHover(null)}
    >
      <text x={label.x} y={label.y} textAnchor={label.anchor} className={`dim-radar-label${active ? ' is-active' : ''}`}>
        {axis.label}
      </text>
      <circle cx={p.x} cy={p.y} r={12} className="dim-radar-hit" />
      <circle cx={p.x} cy={p.y} r={active ? 5.5 : 4} className="dim-radar-dot" />
    </g>
  );
}

function RadarTip({ axis, at, benchmarkLabel }: { axis: RadarAxis; at: { x: number; y: number }; benchmarkLabel: string | null }) {
  return (
    <div
      className="dim-radar-tip"
      aria-hidden="true"
      style={{ left: `${((at.x - VIEW.x) / VIEW.w) * 100}%`, top: `${((at.y - VIEW.y) / VIEW.h) * 100}%` }}
    >
      <b>{axis.label}</b>
      <dl>
        <dt>Score</dt>
        <dd>{axis.value} / 100</dd>
        {benchmarkLabel !== null && (
          <>
            <dt>{benchmarkLabel}</dt>
            <dd>{formatScore(axis.benchmark!)}</dd>
            <dt>Difference</dt>
            <dd className={axis.value >= axis.benchmark! ? 'is-above' : 'is-below'}>
              {signed(axis.value - axis.benchmark!)} pts
            </dd>
          </>
        )}
        <dt>Weight</dt>
        <dd>
          {axis.weight}%{axis.points !== null && <> · {signed(axis.points)} pts</>}
        </dd>
      </dl>
    </div>
  );
}

export function DimensionRadar({ axes, benchmarkLabel }: { axes: RadarAxis[]; benchmarkLabel: string | null }) {
  const [hover, setHover] = useState<string | null>(null);
  const n = axes.length;
  if (n < 3) return null;

  const withBenchmark = benchmarkLabel !== null && axes.every((a) => a.benchmark !== null);
  const index = axes.findIndex((a) => a.key === hover);
  const hovered = index >= 0 ? axes[index] : null;
  const shownBenchmark = withBenchmark ? benchmarkLabel : null;

  return (
    <div className="dim-radar-wrap">
      <svg
        className="dim-radar"
        viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.w} ${VIEW.h}`}
        role="group"
        aria-label={`Dimension profile${withBenchmark ? ` against the ${benchmarkLabel}` : ''}`}
      >
        {RINGS.map((v) => (
          <polygon key={v} points={polygon(axes.map(() => v))} className={`dim-radar-ring${v === 60 || v === 80 ? ' is-band' : ''}`} />
        ))}
        {axes.map((a, i) => {
          const end = point(i, n, 100);
          return <line key={a.key} x1={0} y1={0} x2={end.x} y2={end.y} className="dim-radar-spoke" />;
        })}
        {withBenchmark && <polygon points={polygon(axes.map((a) => a.benchmark!))} className="dim-radar-benchmark" />}
        <polygon points={polygon(axes.map((a) => a.value))} className="dim-radar-shape" />
        {withBenchmark &&
          axes.map((a, i) => {
            const p = point(i, n, a.benchmark!);
            return <circle key={a.key} cx={p.x} cy={p.y} r={2.5} className="dim-radar-benchmark-dot" />;
          })}
        {axes.map((a, i) => (
          <RadarPoint
            key={a.key}
            axis={a}
            i={i}
            n={n}
            active={hover === a.key}
            benchmarkText={withBenchmark ? `, ${benchmarkLabel} ${formatScore(a.benchmark!)}` : ''}
            setHover={setHover}
          />
        ))}
      </svg>
      {hovered && <RadarTip axis={hovered} at={point(index, n, hovered.value)} benchmarkLabel={shownBenchmark} />}
    </div>
  );
}
